import { clsx, type ClassValue } from 'clsx';
import { format, formatDistanceToNow, isToday, isYesterday, parseISO } from 'date-fns';
import { type CountryCode, parsePhoneNumber } from 'libphonenumber-js';
import { twMerge } from 'tailwind-merge';

/** Merge Tailwind classes safely (clsx + tailwind-merge). */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatFileSize(bytes: number): string {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 Bytes';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
}

export function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-US').format(num);
}

export function formatCurrency(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
}

export function formatPercentage(value: number, decimals: number = 1): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

export const dateUtils = {
  formatDate: (date: string | Date): string => {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (isToday(dateObj)) {
      return format(dateObj, 'h:mm a');
    } else if (isYesterday(dateObj)) {
      return 'Yesterday';
    } else {
      return format(dateObj, 'MMM d, yyyy');
    }
  },

  formatDateTime: (date: string | Date): string => {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return format(dateObj, 'MMM d, yyyy h:mm a');
  },

  formatRelativeTime: (date: string | Date): string => {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return formatDistanceToNow(dateObj, { addSuffix: true });
  },

  formatDuration: (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  },
};

export const statusUtils = {
  getStatusColor: (status: string): string => {
    const statusMap: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      processing: 'bg-blue-100 text-blue-800 border-blue-200',
      completed: 'bg-green-100 text-green-800 border-green-200',
      failed: 'bg-red-100 text-red-800 border-red-200',
      cancelled: 'bg-gray-100 text-gray-800 border-gray-200',
      active: 'bg-green-100 text-green-800 border-green-200',
      idle: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      ended: 'bg-gray-100 text-gray-800 border-gray-200',
      expired: 'bg-red-100 text-red-800 border-red-200',
    };
    return statusMap[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  },

  getStatusIcon: (status: string): string => {
    const iconMap: Record<string, string> = {
      pending: '⏳',
      processing: '⚙️',
      completed: '✅',
      failed: '❌',
      cancelled: '🚫',
      active: '🟢',
      idle: '🟡',
      ended: '⚫',
      expired: '🔴',
    };
    return iconMap[status] || '❓';
  },

  formatStatus: (status: string): string => {
    return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
  },
};

const ENGINE_NAMES: Record<string, string> = {
  unstructured: 'Unstructured',
  reducto: 'Reducto',
};

/** Human-readable name for a parse engine key. */
export function engineDisplayName(engine: string): string {
  return ENGINE_NAMES[engine] ?? engine.charAt(0).toUpperCase() + engine.slice(1);
}

// ---------------------------------------------------------------------------
// Field value display formatting
// ---------------------------------------------------------------------------

/** ISO 3166-1 alpha-2 country code → flag emoji (e.g. `'GB'` → 🇬🇧).
 * Built from the two regional-indicator code points; `''` for an unknown code.
 * Note: Windows does not render regional-indicator flags (shows the 2 letters
 * instead) — an acceptable fallback that still names the country. */
function flagEmoji(country: CountryCode | undefined): string {
  if (!country || country.length !== 2) return '';
  const cc = country.toUpperCase();
  return String.fromCodePoint(0x1f1e6 + cc.charCodeAt(0) - 65, 0x1f1e6 + cc.charCodeAt(1) - 65);
}

/** Best-effort libphonenumber parse; never throws. */
function safeParse(input: string, defaultCountry?: CountryCode) {
  try {
    return parsePhoneNumber(input, defaultCountry);
  } catch {
    return undefined;
  }
}

/**
 * Dash-group a phone number for readability and prefix the country flag.
 * Strips existing punctuation first, so it is idempotent:
 *   7 digits             → 332-6481            (local fragment — no country/flag)
 *   10 digits            → 🇺🇸 817-597-6169      (bare NANP — assumed US)
 *   11 digits (leading 1) → 🇺🇸 1-832-399-3900   (flag is area-code-aware: 🇨🇦 for Canada)
 *   valid international   → 🇬🇧 +44 7447 348614   (E.164 digits, grouped per country)
 * The country comes from libphonenumber (`isValid()`-gated); the flag is
 * prefixed only when the country is known. NANP shapes keep the familiar dash
 * grouping; everything else is parsed as E.164 (a leading `+` is implied) and,
 * if VALID, formatted internationally. Anything that doesn't validate is
 * returned UNCHANGED — never mangle an unrecognized value.
 */
export function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '');

  let display: string;
  let country: CountryCode | undefined;

  if (digits.length === 7) {
    // Local fragment — no country context; keep the dash form, no flag.
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  } else if (digits.length === 10) {
    display = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    const parsed = safeParse(digits, 'US'); // bare 10-digit → assume NANP/US
    country = parsed?.isValid() ? parsed.country : undefined;
  } else if (digits.length === 11 && digits.startsWith('1')) {
    display = `1-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
    const parsed = safeParse(`+${digits}`); // detects US vs CA by area code
    country = parsed?.isValid() ? parsed.country : undefined;
  } else {
    const parsed = safeParse(`+${digits}`);
    if (!parsed?.isValid()) return value; // never mangle
    display = parsed.formatInternational();
    country = parsed.country;
  }

  const flag = flagEmoji(country);
  return flag ? `${flag} ${display}` : display;
}

const PHONE_FIELD_RE = /phone|fax|mobile/i;

/**
 * Whole-word match on the canonical monetary field names in the ER ontology.
 *
 * Grounded against the backend's monetary-field registries so we format the
 * fields that are actually money and never mangle a number that isn't:
 *   - `state_field_registry.py` entries with `field_unit="USD"`
 *     (amount, total_amount, open_balance, unit_price, unit_cost)
 *   - `FINANCIAL_FIELDS` in `config/thresholds.py` (the provenance set)
 *   - the transaction-snapshot amount aliases (transaction_amount,
 *     extended_amount, line_total) + reasonable extensions present in code
 *     (amount_due, amount_paid, balance_due, tax_amount, tax_total, subtotal,
 *     discount_amount, fee, revenue)
 *
 * Deliberately EXCLUDED (ambiguous → leave unformatted, per "never mangle"):
 *   - `quantity` (a count, not money)
 *   - bare `discount` / `rate` / `terms` (could be a % or non-currency)
 *   - `tax_id` (an identifier, not an amount)
 * Word boundaries (`\b`) keep `account` / `discount` / `count` /
 * `account_id` from matching the `amount`/`count` substrings.
 */
const CURRENCY_FIELD_RE =
  /\b(amount|amount_due|amount_paid|total|total_amount|subtotal|balance|balance_due|open_balance|cost|unit_cost|price|unit_price|tax_amount|tax_total|line_total|extended_amount|transaction_amount|discount_amount|fee|revenue)\b/i;

const CURRENCY_FORMAT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Format a monetary field value as USD ($16,385.50). Strips a leading `$`
 * and thousands commas first so an already-formatted value passes through
 * unchanged (idempotent). Anything that doesn't parse to a finite number is
 * returned UNCHANGED — mirrors formatPhone's "unrecognized → untouched"
 * philosophy (write-back uses the raw value; this is display-only).
 */
export function formatCurrencyField(value: string): string {
  const cleaned = value.replace(/[$,]/g, '').trim();
  if (cleaned === '') return value;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return value;
  return CURRENCY_FORMAT.format(n);
}

/**
 * Format a raw field value for human display, keyed on the field name.
 *
 * Presentation only — the raw value is preserved for write-back. Dash-groups
 * phone-like fields and renders monetary fields as USD; everything else
 * passes through unchanged. Null/empty → ''.
 */
export function formatFieldValue(
  fieldName: string,
  value: string | null | undefined,
): string {
  if (value == null || value === '') return '';
  if (PHONE_FIELD_RE.test(fieldName)) return formatPhone(value);
  if (CURRENCY_FIELD_RE.test(fieldName)) return formatCurrencyField(value);
  return value;
}
